import MoreVertIcon from '@mui/icons-material/MoreVert';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import { IconButton, Menu, MenuItem, Stack, TableCell } from '@mui/material';
import React, { useState } from 'react';
import { generatePath } from 'react-router';

import { TaskItemDto, TaskStatusDto } from '@/api/generated';
import {
  ExpandableNameCell,
  ExpandableTableRow,
} from '@/components/table/expandable/ExpandableTableRow.comp.tsx';
import { CustomTableEventModel } from '@/components/table/model/CutomTableEvent.model.ts';
import { TableRowLink } from '@/components/table/TableRowLink.comp.tsx';
import { customTableEventDispatcher } from '@/components/table/util/customTableEventDispatcher.util.ts';
import { SmartDate } from '@/components/ui/smartDate/SmartDate.comp.tsx';
import { StatusPill } from '@/components/ui/statusPill/StatusPill.comp.tsx';
import { isTaskFinished, taskStatusTone } from '@/components/ui/statusPill/statusTone.util.ts';
import { UserAvatar } from '@/components/ui/userAvatar/UserAvatar.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { resolveNextTaskStatus } from '@/views/tasks/details/components/header/util/resolveNextTaskStatus.util.ts';
import { useUpdateTaskStatus } from '@/views/tasks/details/components/header/util/useUpdateTaskStatus.util.ts';
import { TasksListRowDetails } from '@/views/tasks/list/components/table/components/TasksListRowDetails.comp.tsx';

type Props = {
  onCustomAction?: (row: TaskItemDto) => Promise<void> | void;
  row: TaskItemDto;
};

export const TasksListTableRow = ({ onCustomAction, row }: Props) => {
  const { t } = useTranslationWithPrefix('views.tasks.list.table.body');
  const { push } = useAppNavigation();

  const detailsPath = generatePath(RouteEnum.TASK_DETAILS, { taskId: row.taskId });

  return (
    <ExpandableTableRow
      details={<TasksListRowDetails detailsPath={detailsPath} row={row} />}
      onRowClick={onCustomAction ? () => onCustomAction(row) : () => push(detailsPath)}
    >
      <ExpandableNameCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {onCustomAction ? row.title : <TableRowLink to={detailsPath}>{row.title}</TableRowLink>}
          {row.changedSinceLastVisit && (
            <SmsFailedOutlinedIcon style={{ color: themeColors.red, fontSize: 14 }} />
          )}
        </Stack>
      </ExpandableNameCell>
      <TableCell>
        <UserAvatar person={row.creator} role={t('creator')} />
      </TableCell>
      <TableCell>
        <UserAvatar person={row.assignedUser} role={t('assignedUser')} />
      </TableCell>
      <TableCell>
        <SmartDate date={row.dueDate} isFinished={isTaskFinished(row.status)} variant="due" />
      </TableCell>
      <TableCell>{t(`resourceTypeOptions.${row.contextResourceType}`)}</TableCell>
      <TableCell>
        <StatusPill
          label={t(`taskStatusOptions.${row.status}`)}
          tone={taskStatusTone[row.status]}
        />
      </TableCell>
      <TableCell>
        <TaskActionsMenu row={row} />
      </TableCell>
    </ExpandableTableRow>
  );
};

type TaskActionsMenuProps = {
  row: TaskItemDto;
};

const TaskActionsMenu: React.FC<TaskActionsMenuProps> = ({ row }) => {
  const { updateTaskStatus } = useUpdateTaskStatus();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { t: tMain } = useTranslationWithPrefix('views.tasks.details.main');
  const { userId } = useUserContext();

  const isOwner = row.creator.userId === userId || row.creator.firstName === 'System';
  const nextTaskStatuses = resolveNextTaskStatus(row.status, isOwner);

  if (nextTaskStatuses.length < 1) return null;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateStatus = async (e: React.MouseEvent<HTMLLIElement>, status: TaskStatusDto) => {
    e.stopPropagation();
    await updateTaskStatus({ status: status, taskId: row.taskId });
    customTableEventDispatcher(CustomTableEventModel.REFRESH);
    handleMenuClose();
  };
  return (
    <>
      <IconButton aria-label="more" id="long-button" onClick={handleMenuClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} onClose={handleMenuClose} open={open}>
        {nextTaskStatuses.map(status => (
          <MenuItem key={status} onClick={e => handleUpdateStatus(e, status)}>
            {tMain(`nextStatus.${status}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
